import { useState } from 'react';
import { Play } from 'lucide-react';
import type { VideoBlock } from '@/types/blocks';

const ASPECT_RATIOS = {
  horizontal: '16 / 9',
  vertical: '9 / 16',
  square: '1 / 1',
} as const;

function getYouTubeId(url: string): string {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([^&\n?#/]+)/);
  return match?.[1] ?? '';
}

function getVimeoId(url: string): string {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match?.[1] ?? '';
}

export function VideoPlayerBlock({ block, className = 'w-full aspect-video' }: { block: VideoBlock; className?: string }) {
  const url = block.url.trim();
  const [started, setStarted] = useState(false);
  const aspectRatio = ASPECT_RATIOS[block.aspectRatio ?? 'horizontal'];
  const playerStyle = { aspectRatio };

  const poster = block.posterUrl?.trim();
  const cover = poster ? (
    <button
      type="button"
      onClick={() => setStarted(true)}
      className={`${className} group relative block overflow-hidden bg-black`}
      style={playerStyle}
      aria-label="Reproduzir vídeo"
    >
      <img src={poster} alt="Capa do vídeo" className="h-full w-full object-cover" />
      <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/35">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-lg">
          <Play className="ml-1 h-6 w-6 fill-current" aria-hidden="true" />
        </span>
      </span>
    </button>
  ) : null;

  if (!url) {
    return (
      <div className={`${className} flex items-center justify-center border border-dashed border-border text-sm text-muted`} style={playerStyle}>
        Nenhum vídeo configurado
      </div>
    );
  }

  if (block.provider === 'youtube') {
    const videoId = getYouTubeId(url);
    if (!videoId) {
      return (
        <div className={`${className} flex items-center justify-center border border-dashed border-border text-sm text-muted`} style={playerStyle}>
          URL do YouTube inválida
        </div>
      );
    }
    if (cover && !started) return cover;
    return (
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        className={className}
        style={playerStyle}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        title="Vídeo"
      />
    );
  }

  if (block.provider === 'vimeo') {
    const videoId = getVimeoId(url);
    if (!videoId) {
      return (
        <div className={`${className} flex items-center justify-center border border-dashed border-border text-sm text-muted`} style={playerStyle}>
          URL do Vimeo inválida
        </div>
      );
    }
    if (cover && !started) return cover;
    return (
      <iframe
        src={`https://player.vimeo.com/video/${videoId}`}
        className={className}
        style={playerStyle}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title="Vídeo"
      />
    );
  }

  return (
    <video
      src={url}
      className={className}
      style={playerStyle}
      poster={poster || undefined}
      controls
      preload="metadata"
      playsInline
    />
  );
}
